import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MapPin, Package, BarChart3, Edit, Plus, Loader2 } from "lucide-react";
import { GeofenceMap, type GeoFenceData } from "@/components/geofence-map";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getTenantId } from "@/lib/auth";
import {
  createStockPileSchema,
  type StockArea,
  type StockPile,
  type StockOverview,
  type InsertStockPile,
  type Client
} from "../../../shared/schema";
import { useState } from "react";

interface StockOverview {
  area: StockArea;
  stacks: (Stack & { allocatedQuantity: number; availableQuantity: number })[];
  totalCapacity: number;
  totalCurrentStock: number;
  totalAllocated: number;
  totalAvailable: number;
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'inactive':
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    case 'maintenance':
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'depleted':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'quarantined':
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    default:
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  }
};

const formatQuantity = (quantity: number | string, unit: string) => {
  const num = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
  return `${num.toLocaleString()} ${unit}`;
};

export default function StockAreaDetails() {
  const params = useParams();
  const areaId = parseInt(params.id!);
  const { toast } = useToast(); 
  const currentTenantId = getTenantId() || "";

  // State for edit pile dialog
  const [showEditPileDialog, setShowEditPileDialog] = useState(false);
  const [editingPile, setEditingPile] = useState<StockPile | null>(null);

  // Form for editing stock pile
  const pileForm = useForm<InsertStockPile>({
    resolver: zodResolver(createStockPileSchema),
    defaultValues: {
      tenantId: currentTenantId,
      areaId: undefined as any,
      pileCode: "",
      name: "",
      description: "",
      materialType: "",
      materialGrade: "",
      clientId: undefined,
      clientName: "",
      currentQuantity: "0",
      unit: "kg",
      status: "active",
      createdDate: new Date() as any,
    }
  });

  const [, setLocation] = useLocation();

  const handleAddStack = () => {
    setLocation(`/stock-management?tab=stacks&action=createStack&areaId=${areaId}`);
  };

  const handleEditStack = (stackId: number) => {
    setLocation(`/stock-management?tab=stacks&action=editStack&stackId=${stackId}`);
  };

  const { data: area, isLoading: areaLoading } = useQuery<StockArea>({
    queryKey: [`/api/stock/areas/${areaId}`],
    enabled: !!areaId,
  });

  const { data: stacks = [], isLoading: stacksLoading } = useQuery<Stack[]>({
    queryKey: [`/api/stock/stacks?areaId=${areaId}`],
    enabled: !!areaId,
  });

  const { data: areaOverview } = useQuery<StockOverview[]>({
    queryKey: ['/api/stock/overview'],
  });

  const { data: mapConfig, isLoading: mapConfigLoading } = useQuery<{ tomtomApiKey: string }>({
    queryKey: ['/api/config/map'],
  });

  const updateGeofenceMutation = useMutation({
    mutationFn: async (data: { geoFence: GeoFenceData; centerLatitude: number; centerLongitude: number }) => {
      return apiRequest('PUT', `/api/stock/areas/${areaId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/stock/areas/${areaId}`] });
      toast({
        title: "Geofence updated",
        description: "The stock area geofence has been saved successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update geofence",
        variant: "destructive",
      });
    },
  });

  const handleGeofenceSave = (geofence: GeoFenceData, center: { lat: number; lng: number }) => {
    updateGeofenceMutation.mutate({
      geoFence: geofence,
      centerLatitude: center.lat,
      centerLongitude: center.lng,
    });
  };

  // Fetch stock areas for the area selector in edit dialog
  const { data: stockAreas = [] } = useQuery<StockArea[]>({
    queryKey: ['/api/stock/areas'],
  });

  // Fetch clients for the client selector in edit dialog
  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ['/api/clients'],
  });

  // Update pile mutation
  const updatePileMutation = useMutation({
    mutationFn: ({ pileId, pileData }: { pileId: number; pileData: InsertStockPile }) =>
      apiRequest('PUT', `/api/stock/piles/${pileId}`, pileData),
    onSuccess: () => {
      toast({ title: "Success", description: "Stock pile updated successfully" });
      setShowEditPileDialog(false);
      setEditingPile(null);
      pileForm.reset();
      queryClient.invalidateQueries({ queryKey: [`/api/stock/areas/${areaId}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/overview'] });
      queryClient.invalidateQueries({ queryKey: [`/api/stock/piles?areaId=${areaId}`] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update stock pile",
        variant: "destructive"
      });
    }
  });

  // Update pile handler
  const onUpdatePile = (data: InsertStockPile) => {
    if (editingPile) {
      console.log('Updating pile with data:', data);
      console.log('Edit form errors:', pileForm.formState.errors);
      updatePileMutation.mutate({ pileId: editingPile.id, pileData: data });
    }
  };

  if (areaLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading area details...</p>
        </div>
      </div>
    );
  }

  if (!area) {
    return (
      <div className="text-center py-12">
        <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Stock Area Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested stock area could not be found.</p>
        <Link href="/stock-management">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Stock Management
          </Button>
        </Link>
      </div>
    );
  }

  const overview = areaOverview?.find(o => o.area.id === areaId);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <Link href="/stock-management">
            <Button variant="outline" size="sm" data-testid="button-back-to-stock">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Stock Management
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)]" data-testid="text-area-name">{area.name}</h1>
            <p className="text-muted-foreground">Area Code: {area.areaCode}</p>
          </div>
        </div>
        <Badge variant="outline" className={getStatusColor(area.status)} data-testid="badge-area-status">
          {area.status}
        </Badge>
      </div>

      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Capacity</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-400" data-testid="text-total-capacity">
                {formatQuantity(overview.totalCapacity, area.capacityUnit || 'tons')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Current Stock</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-400" data-testid="text-current-stock">
                {formatQuantity(overview.totalCurrentStock, area.capacityUnit || 'tons')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Available</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-400" data-testid="text-available-stock">
                {formatQuantity(overview.totalAvailable, area.capacityUnit || 'tons')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Allocated</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-400" data-testid="text-allocated-stock">
                {formatQuantity(overview.totalAllocated, area.capacityUnit || 'tons')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Utilization</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-muted-foreground" data-testid="text-utilization">
                {overview.totalCapacity > 0 ? 
                  `${((overview.totalCurrentStock / overview.totalCapacity) * 100).toFixed(1)}%` : 
                  'N/A'
                }
              </div>
              {overview.totalCapacity > 0 && (
                <Progress 
                  value={(overview.totalCurrentStock / overview.totalCapacity) * 100} 
                  className="mt-2"
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Area Details</TabsTrigger>
          <TabsTrigger value="stacks">Stacks ({stacks.length})</TabsTrigger>
          <TabsTrigger value="location">Location & Geo-fence</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)]">Area Information</CardTitle>
              <CardDescription>Basic details about this stock area</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Area Code</label>
                    <p className="text-sm" data-testid="text-detail-area-code">{area.areaCode}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Type</label>
                    <p className="text-sm capitalize" data-testid="text-detail-area-type">{area.areaType}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Status</label>
                    <p className="text-sm" data-testid="text-detail-status">{area.status}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Access Level</label>
                    <p className="text-sm capitalize" data-testid="text-detail-access">{area.accessLevel || 'Standard'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Maximum Capacity</label>
                    <p className="text-sm" data-testid="text-detail-capacity">
                      {area.maxCapacity ? formatQuantity(area.maxCapacity, area.capacityUnit || 'kg') : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Created</label>
                    <p className="text-sm" data-testid="text-detail-created">
                      {area.createdAt ? format(new Date(area.createdAt), 'PPP') : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Last Updated</label>
                    <p className="text-sm" data-testid="text-detail-updated">
                      {area.updatedAt ? format(new Date(area.updatedAt), 'PPP') : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
              {area.description && (
                <div>
                  <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Description</label>
                  <p className="text-sm mt-1" data-testid="text-detail-description">{area.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stacks" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-[hsl(187,100%,42%)]">Stacks</CardTitle>
                  <CardDescription>All stacks located in this area</CardDescription>
                </div>
                <Button 
                    size="sm" 
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="button-add-stack"
                    onClick={handleAddStack}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Stack
                  </Button>
              </div>
            </CardHeader>
            <CardContent>
              {stacksLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading stacks...</p>
                </div>
              ) : stacks.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Stack Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>Current Stock</TableHead>
                      <TableHead>Available</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stacks.map((stack) => {
                      const available = parseFloat(stack.currentQuantity.toString()) - parseFloat(stack.reservedQuantity.toString());
                      return (
                        <TableRow key={stack.id}>
                          <TableCell className="font-medium" data-testid={`text-stack-code-${stack.id}`}>
                            {stack.stackCode}
                          </TableCell>
                          <TableCell data-testid={`text-stack-name-${stack.id}`}>{stack.name}</TableCell>
                          <TableCell>
                            <div>
                              <div data-testid={`text-stack-material-${stack.id}`}>{stack.materialType}</div>
                              {stack.materialGrade && (
                                <div className="text-xs text-muted-foreground">Grade: {stack.materialGrade}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell data-testid={`text-stack-current-${stack.id}`}>
                            {formatQuantity(stack.currentQuantity, stack.unit)}
                          </TableCell>
                          <TableCell data-testid={`text-stack-available-${stack.id}`}>
                            {formatQuantity(available, stack.unit)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getStatusColor(stack.status)} data-testid={`badge-stack-status-${stack.id}`}>
                              {stack.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingPile(stack);
                                pileForm.reset({
                                  tenantId: stack.tenantId,
                                  areaId: stack.areaId,
                                  pileCode: stack.pileCode,
                                  name: stack.name,
                                  description: stack.description || "",
                                  materialType: stack.materialType,
                                  materialGrade: stack.materialGrade || "",
                                  currentQuantity: stack.currentQuantity.toString(),
                                  unit: stack.unit,
                                  status: stack.status,
                                  createdDate: stack.createdDate,
                                });
                                setShowEditPileDialog(true);
                              }}
                              data-testid={`button-edit-pile-${stack.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Stacks Found</h3>
                  <p className="text-muted-foreground mb-4">No stacks are currently located in this area.</p>
                  <Button 
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="button-create-first-stack"
                      onClick={handleAddStack}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Stack
                    </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="location" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)]">Location & Geographic Information</CardTitle>
              <CardDescription>Geographic boundaries and location data for this area. Draw a geofence to define the area boundary.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Center Coordinates</label>
                  <p className="text-sm" data-testid="text-coordinates">
                    {area.centerLatitude && area.centerLongitude 
                      ? `${area.centerLatitude}, ${area.centerLongitude}`
                      : 'Not specified'
                    }
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[hsl(187,100%,42%)]">Geo-fence Status</label>
                  <p className="text-sm" data-testid="text-geofence">
                    {area.geoFence ? (
                      <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                        Defined
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                        Not defined
                      </Badge>
                    )}
                  </p>
                </div>
              </div>
              
              {mapConfigLoading ? (
                <div className="h-[500px] bg-muted rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 text-muted-foreground mx-auto mb-2 animate-spin" />
                    <p className="text-sm text-muted-foreground">Loading map...</p>
                  </div>
                </div>
              ) : mapConfig?.tomtomApiKey ? (
                <GeofenceMap
                  tomtomApiKey={mapConfig.tomtomApiKey}
                  initialGeofence={area.geoFence as GeoFenceData | null}
                  centerLat={area.centerLatitude}
                  centerLng={area.centerLongitude}
                  onSave={handleGeofenceSave}
                  height="500px"
                />
              ) : (
                <div className="h-[500px] bg-muted rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Map configuration not available</p>
                    <p className="text-xs text-muted-foreground mt-1">Please configure the TomTom API key</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Pile Dialog */}
      <Dialog open={showEditPileDialog} onOpenChange={setShowEditPileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Stock Pile</DialogTitle>
          </DialogHeader>

          <Form {...pileForm}>
            <form
              onSubmit={(e) => {
                pileForm.handleSubmit(
                  (data) => onUpdatePile({ id: editingPile?.id, ...data }),
                  (errors) => {
                    console.error('Edit pile validation failed:', errors);
                    toast({
                      title: "Validation Error",
                      description: "Please fix the highlighted fields.",
                      variant: "destructive",
                    });
                  }
                )(e);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="areaId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area *</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        value={
                          field.value !== undefined && field.value !== null
                            ? String(field.value)
                            : undefined
                        }
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-pile-area">
                            <SelectValue placeholder="Select area" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {stockAreas.length === 0 ? (
                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                              No areas available. Create one first.
                            </div>
                          ) : (
                            stockAreas.map((area) => (
                              <SelectItem key={area.id} value={String(area.id)}>
                                {area.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="pileCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pile Code</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., A001-P1" data-testid="input-edit-pile-code" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={pileForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., Iron Ore Pile 1" data-testid="input-edit-pile-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="materialType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Material Type</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Iron Ore, Coal, Sand" data-testid="input-edit-pile-material" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="materialGrade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Material Grade</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Premium, Standard" data-testid="input-edit-pile-grade" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Client Association (optional) */}
              <FormField
                control={pileForm.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client (Optional)</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        const clientId = value === "none" ? undefined : parseInt(value);
                        field.onChange(clientId);
                        const selectedClient = clients.find(c => c.id === clientId);
                        pileForm.setValue("clientName", selectedClient?.name || "");
                      }}
                      value={
                        field.value === undefined || field.value === null
                          ? "none"
                          : String(field.value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-pile-client">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No specific client</SelectItem>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={String(client.id)}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="currentQuantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quantity</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          placeholder="1000"
                          data-testid="input-edit-pile-quantity"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-pile-unit">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="kg">Kilograms</SelectItem>
                          <SelectItem value="tons">Tons</SelectItem>
                          <SelectItem value="m3">Cubic Meters</SelectItem>
                          <SelectItem value="liters">Liters</SelectItem>
                          <SelectItem value="pieces">Pieces</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={pileForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Optional description..." data-testid="input-edit-pile-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditPileDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updatePileMutation.isPending}
                  data-testid="button-update-pile"
                >
                  {updatePileMutation.isPending ? 'Updating...' : 'Update Pile'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
